import { useNamedHeader } from 'hooks/useNamedHeader';
import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	editSubCategoryInitialState,
	EditSubCategoryContext,
	TVisibleForm,
	TTypeOfModalWindow,
} from './editSubCategoryContext';
import { editSubCategoryReducer } from './editSubCategoryReducer';
import { EditSubCategoryActionTypes } from './editSubCategoryActions';
import { useFetchData } from 'hooks/useFetchData';
import {
	ICreateSubCategoryValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TFileId,
	TLanguageIDs,
	IDefaultLanguage,
	IGetSubCategoryInfo,
	IUpdateSubCategory,
} from 'types';
import { getSubCategoryInfo } from 'backend/subcategories';

const EditSubCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editSubCategoryReducer, editSubCategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const subCategoryID = params.get('subCategoryID');

	const { response, loading, refetch } = useFetchData<IGetSubCategoryInfo, string>(
		subCategoryID ? getSubCategoryInfo : null,
		subCategoryID || '',
	);

	useNamedHeader(response?.data.subCategory.name);

	useEffect(() => {
		if (response) {
			dispatch({
				type: EditSubCategoryActionTypes.SET_ALL_DATA,
				payload: {
					subCategoryID: subCategoryID ? subCategoryID : '',
					filePath: response.data.subCategory.filePath,
					preSignedUrl: response.data.subCategory.preSignedUrl,
					initialIDSelectedCategory: response.data.subCategory.categoryId,
					initialNameSelectedCategory: response.data.subCategory.categoryName, // If they make category selection active then remove that.
					initialIDSelectedCustomer: response.data.subCategory.customerId,
					nameOfSubCategory: response.data.subCategory.name ? response.data.subCategory.name : '',
					initialIDsSelectedRestaurant: response.data.subCategory.restaurantsSubCategories.map((el) => el.id),
					initialIDsOfRestaurantsWhereToHide: response.data.subCategory.restaurantsSubCategories
						.filter((el) => el.isHidden)
						.map((el) => el.id),
					initialIDsOfRestaurantsAvailableForRepetition: response.data.subCategory.restaurantsSubCategories
						.filter((el) => el.isAvailableForRepetition)
						.map((el) => el.id),
					dishesOfSubcategory: response.data.subCategory.allDishes,
					activationSchedule: response.data.subCategory.activationCron || '',
					deactivationSchedule: response.data.subCategory.deactivationCron || '',
					translations: response.data.subCategory.translations,
				},
			});
			dispatch({
				type: EditSubCategoryActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						IDSelectedCustomer: response.data.subCategory.customerId,
						IDsSelectedRestaurant: response.data.subCategory.restaurantsSubCategories.map((el) => el.id),
						nameOfSubCategory: response.data.subCategory.name ? response.data.subCategory.name : '',
						IDSelectedCategory: response.data.subCategory.categoryId,
						IDsOfRestaurantsWhereToHide: response.data.subCategory.restaurantsSubCategories
							.filter((el) => el.isHidden)
							.map((el) => el.id),
						IDsOfRestaurantsAvailableForRepetition: response.data.subCategory.restaurantsSubCategories
							.filter((el) => el.isAvailableForRepetition)
							.map((el) => el.id),
						filePath: response.data.subCategory.filePath,
						activationSchedule: response.data.subCategory.activationCron || '',
						deactivationSchedule: response.data.subCategory.deactivationCron || '',
						translations: response.data.subCategory.translations,
					},
				},
			});
		}
	}, [response, subCategoryID]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditSubCategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const refetchData = (value: boolean) => {
		dispatch({
			type: EditSubCategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	};

	const changeLoaderStatus = useCallback((value: boolean) => {
		dispatch({
			type: EditSubCategoryActionTypes.CHANGE_LOADER_STATUS,
			payload: {
				isLoading: value,
			},
		});
	}, []);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: EditSubCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const deletePhoto = useCallback(() => dispatch({ type: EditSubCategoryActionTypes.DELETE_PHOTO }), []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfoWithStatus[]) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback(
		(value: string[]) => {
			const updateIDsOfRestaurantsWhereToHide = state.IDsOfRestaurantsWhereToHide.filter((el) =>
				value.includes(el),
			);
			const updateIDsOfRestaurantsAvailableForRepetition = state.IDsOfRestaurantsAvailableForRepetition.filter(
				(el) => value.includes(el),
			);
			dispatch({
				type: EditSubCategoryActionTypes.SET_IDS_RESTAURANT,
				payload: {
					IDsSelectedRestaurant: value,
					IDsOfRestaurantsWhereToHide: updateIDsOfRestaurantsWhereToHide,
					IDsOfRestaurantsAvailableForRepetition: updateIDsOfRestaurantsAvailableForRepetition,
				},
			});
		},
		[state.IDsOfRestaurantsWhereToHide, state.IDsOfRestaurantsAvailableForRepetition],
	);

	const changeNameOfSubCategory = useCallback((value: string) => {
		dispatch({
			type: EditSubCategoryActionTypes.CHANGE_NAME_OF_SUBCATEGORY,
			payload: {
				nameOfSubCategory: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				categories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	}, []);

	const setIDsOfRestaurantsWhereToHide = useCallback((value: string[]) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_IDS_OF_RESTAURANTS_WHERE_TO_HIDE,
			payload: {
				IDsOfRestaurantsWhereToHide: value,
			},
		});
	}, []);

	const setIDsOfRestaurantsAvailableForRepetition = useCallback((value: string[]) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_IDS_OF_RESTAURANTS_AVAILABLE_FOR_REPETITION,
			payload: {
				IDsOfRestaurantsAvailableForRepetition: value,
			},
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditSubCategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: EditSubCategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setDefaultCustomerLanguage = useCallback((value: Nullable<IDefaultLanguage>) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_DEFAULT_CUSTOMER_LANGUAGE,
			payload: {
				defaultCustomerLanguage: value,
			},
		});
	}, []);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: EditSubCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: IUpdateSubCategory | null) => {
		dispatch({
			type: EditSubCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditSubCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<ICreateSubCategoryValidationError> | null) => {
		dispatch({
			type: EditSubCategoryActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditSubCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<EditSubCategoryContext.Provider
			value={{
				refetchData,
				changeLoaderStatus,
				changeVisibleForm,
				setFileId,
				deletePhoto,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfSubCategory,
				setFetchedCategories,
				setIDCategory,
				setIDsOfRestaurantsWhereToHide,
				setIDsOfRestaurantsAvailableForRepetition,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setDefaultCustomerLanguage,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				isLoading: state.isLoading,
				subCategoryID: state.subCategoryID,
				visibleForm: state.visibleForm,
				filePath: state.filePath,
				preSignedUrl: state.preSignedUrl,
				fileId: state.fileId,
				fetchedCustomers: state.fetchedCustomers,
				initialIDSelectedCustomer: state.initialIDSelectedCustomer,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				initialIDsSelectedRestaurant: state.initialIDsSelectedRestaurant,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfSubCategory: state.nameOfSubCategory,
				fetchedCategories: state.fetchedCategories,
				initialIDSelectedCategory: state.initialIDSelectedCategory,
				initialNameSelectedCategory: state.initialNameSelectedCategory, // If they make category selection active then remove that.
				IDSelectedCategory: state.IDSelectedCategory,
				initialIDsOfRestaurantsWhereToHide: state.initialIDsOfRestaurantsWhereToHide,
				IDsOfRestaurantsWhereToHide: state.IDsOfRestaurantsWhereToHide,
				initialIDsOfRestaurantsAvailableForRepetition: state.initialIDsOfRestaurantsAvailableForRepetition,
				IDsOfRestaurantsAvailableForRepetition: state.IDsOfRestaurantsAvailableForRepetition,
				dishesOfSubcategory: state.dishesOfSubcategory,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditSubCategoryContext.Provider>
	);
};

export default EditSubCategoryState;
